import React, { useEffect, useState, useMemo } from "react";
import toast from "react-hot-toast";
import { updateVendor } from "../../Api/Vendors/Vendors";
import Loader from "../../../../../components/Loader/Loader";
import useVendorById from "../../hooks/useVendorById";
import useVendorReferrals from "../../hooks/useVendorReferrals";
import { Formik } from "formik";
import { vendorSchema } from "../../util/schema";
import { mapToUpdateVendor } from "../../util/mapper";
import VendorForm from "../VendorForm/VendorForm";
import CustomerNewServices from "../../../../../Services/CustomerV1Services.js";
import { findMatchingNames } from "../../../../../utils/nameValidation.ts";
import { DuplicateNameDialog } from "../../../../../components/DuplicateNameDialog/DuplicateNameDialog.tsx";

const UpdateVendor = ({ vendorID }) => {
  const [referralAll, setReferralAll] = useState([]);
  const [existingVendors, setExistingVendors] = useState([]);
  const [dialogState, setDialogState] = useState({ open: false, matches: [], matchType: "fuzzy" });
  const [pendingSubmission, setPendingSubmission] = useState(null);

  const { data: vendor, loading: loadingVendor, isRequestCompleted: vendorRequestCompleted } = useVendorById(vendorID);

  const {
    data: vendorReferrals,
    loading: loadingReferrals,
    isRequestCompleted: referralRequestCompleted,
  } = useVendorReferrals();

  useEffect(() => {
    setReferralAll(
      vendorReferrals?.map(referral => {
        return { label: referral.NAME, value: referral.ID.toString() };
      }),
    );
  }, [loadingVendor, loadingReferrals, vendorReferrals]);

  useEffect(() => {
    // Fetch all vendors for duplicate checking
    const fetchAllVendors = async () => {
      try {
        const response = await CustomerNewServices.listCustomers();
        if (response.status === 200) {
          setExistingVendors(response?.data || []);
        }
      } catch (e) {
        console.error("Error fetching vendors:", e);
      }
    };
    fetchAllVendors();
  }, []);

  const existingVendorNames = useMemo(() => {
    return existingVendors.map(v => ({ id: v.VENDOR_ID, name: v.VENDOR_NAME }));
  }, [existingVendors]);

  const loading = loadingVendor || loadingReferrals || !vendorRequestCompleted || !referralRequestCompleted;

  const handleVendorUpdate = async (values, { resetForm, setSubmitting }) => {
    // Check for fuzzy matches before updating (exact matches already blocked by form validation)
    const vendorName = values.VENDOR_NAME?.trim();
    if (vendorName) {
      const numericVendorId = parseInt(vendorID, 10);
      const result = findMatchingNames(vendorName, existingVendorNames, numericVendorId);

      if (result.fuzzyMatches.length > 0) {
        setPendingSubmission({ values, resetForm, setSubmitting });
        setDialogState({
          open: true,
          matches: result.fuzzyMatches,
          matchType: "fuzzy",
        });
        setSubmitting(false);
        return;
      }
    }

    await submitVendorUpdate(values, resetForm);
    setSubmitting(false);
  };

  const submitVendorUpdate = async (values, resetForm) => {
    const updatedData = {};
    const relevantFields = [
      "REFERRAL",
      "REFERRAL_COMMISSION_RATE",
      "REFERRAL_COMMISSION_DURATION",
      "REFERRAL_PARTNER_ID",
      "REFERRAL_PAYMENT_METHOD",
      "REFERRAL_PARTNER_TYPE",
      "REFERRAL_CUSTOM_COMMISSION_RATE",
      "REFERRAL_2",
      "REFERRAL_2_COMMISSION_RATE",
      "REFERRAL_2_COMMISSION_DURATION",
      "REFERRAL_2_PARTNER_ID",
      "REFERRAL_2_PAYMENT_METHOD",
      "REFERRAL_2_PARTNER_TYPE",
      "REFERRAL_2_CUSTOM_COMMISSION_RATE",
    ];

    let relevantFieldsUpdated = false;

    Object.keys(values).forEach(key => {
      if (isFieldUpdated(key, values)) {
        updatedData[key] = values[key];
      }
    });

    relevantFields.forEach(key => {
      if (isFieldUpdated(key, values)) {
        relevantFieldsUpdated = true;
      }
    });

    if (relevantFieldsUpdated) {
      relevantFields.forEach(key => {
        updatedData[key] = values[key];
      });
    }
    const mappedData = mapToUpdateVendor(updatedData);
    const result = await updateVendor(vendorID, mappedData, toast);

    if (result?.success) {
      resetForm({ values });
    }
  };

  const handleDialogProceed = async () => {
    if (!pendingSubmission) return;

    const { values, resetForm, setSubmitting } = pendingSubmission;

    setDialogState({ open: false, matches: [], matchType: "fuzzy" });
    setSubmitting(true);

    await submitVendorUpdate(values, resetForm);

    setSubmitting(false);
    setPendingSubmission(null);
  };

  const handleDialogClose = () => {
    setDialogState({ open: false, matches: [], matchType: "fuzzy" });
    setPendingSubmission(null);
  };

  const initialValues = {
    VENDOR_ID: `V${vendorID}`,
    REFERRAL: vendor?.REFERRALS[0]?.REFERRAL_PARTNER_NAME ?? null,
    REFERRAL_COMMISSION_RATE: vendor?.REFERRALS[0]?.COMMISSION_RATE ?? null,
    REFERRAL_COMMISSION_DURATION: vendor?.REFERRALS[0]?.COMMISSION_ELIGIBILITY_END_AT
      ? new Date(vendor?.REFERRALS[0]?.COMMISSION_ELIGIBILITY_END_AT).toISOString().split("T")[0]
      : null,
    REFERRAL_PARTNER_ID: vendor?.REFERRALS[0]?.PARTNER_ID ?? null,
    REFERRAL_PAYMENT_METHOD: vendor?.REFERRALS[0]?.PAYMENT_METHOD ?? null,
    REFERRAL_PARTNER_TYPE: vendor?.REFERRALS[0]?.PARTNER_TYPE ?? null,
    REFERRAL_CUSTOM_COMMISSION_RATE: vendor?.REFERRALS[0]?.CUSTOM_COMMISSION_RATE ?? null,
    REFERRAL_2: vendor?.REFERRALS[1]?.REFERRAL_PARTNER_NAME || null,
    REFERRAL_2_COMMISSION_RATE: vendor?.REFERRALS[1]?.COMMISSION_RATE ?? null,
    REFERRAL_2_COMMISSION_DURATION: vendor?.REFERRALS[1]?.COMMISSION_ELIGIBILITY_END_AT
      ? new Date(vendor?.REFERRALS[1]?.COMMISSION_ELIGIBILITY_END_AT).toISOString().split("T")[0]
      : null,
    REFERRAL_2_PARTNER_ID: vendor?.REFERRALS[1]?.PARTNER_ID ?? null,
    REFERRAL_2_PAYMENT_METHOD: vendor?.REFERRALS[1]?.PAYMENT_METHOD ?? null,
    REFERRAL_2_PARTNER_TYPE: vendor?.REFERRALS[1]?.PARTNER_TYPE ?? null,
    REFERRAL_2_CUSTOM_COMMISSION_RATE: vendor?.REFERRALS[1]?.CUSTOM_COMMISSION_RATE ?? null,
    SPECIFIC_POINT_OF_CONTACT_NAME: vendor?.SPECIFIC_POINT_OF_CONTACT_NAME ?? "",
    SPECIFIC_POINT_OF_CONTACT_EMAIL: vendor?.SPECIFIC_POINT_OF_CONTACT_EMAIL ?? "",
    VENDOR_NAME: vendor?.VENDOR_NAME ?? "",
    TIER: vendor?.TIER ?? "",
    INITIAL_COMMISSION_RATE: vendor?.INITIAL_COMMISSION_RATE ?? null,
    FINAL_COMMISSION_RATE: vendor?.FINAL_COMMISSION_RATE ?? null,
    ENGAGEMENT_FEE: vendor?.ENGAGEMENT_FEE ?? null,
    CURRENT_RECOVERY: vendor?.CURRENT_RECOVERY ?? 0,
    NOTES: vendor?.NOTES ?? "",
    RECOVERY_THRESHOLD: vendor?.RECOVERY_THRESHOLD ?? null,
    AGREEMENT_SIGNED_DATE: vendor?.AGREEMENT_SIGNED_DATE
      ? new Date(vendor?.AGREEMENT_SIGNED_DATE).toISOString().split("T")[0]
      : "",
    KICK_OFF: vendor?.KICK_OFF ? new Date(vendor?.KICK_OFF).toISOString().split("T")[0] : null,
    PHONE: vendor?.PHONE ?? "",
    BILLING_NAME: vendor?.BILLING_NAME ?? "",
    BILLING_EMAIL: vendor?.BILLING_EMAIL ?? "",
    BILLING_ENTITY: vendor?.BILLING_ENTITY ?? "",
    VENDOR_TYPE: vendor?.VENDOR_TYPE ?? "",
    CLIENT_SUCCESS_MANAGER_NAME: vendor?.CLIENT_SUCCESS_MANAGER_NAME ?? "",
    CLIENT_SUCCESS_MANAGER_EMAIL: vendor?.CLIENT_SUCCESS_MANAGER_EMAIL ?? "",
  };

  const isFieldUpdated = (fieldName, values) => {
    return initialValues[fieldName] !== values[fieldName];
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <div className="container mt-4 ">
        <Formik
          initialValues={initialValues}
          onSubmit={handleVendorUpdate}
          validationSchema={vendorSchema}
          validateOnMount={true}
        >
          {({ isSubmitting, dirty, isValid, values }) => (
            <VendorForm
              isSubmitting={isSubmitting}
              dirty={dirty}
              isValid={isValid}
              values={values}
              referralAll={referralAll}
              setReferralAll={setReferralAll}
              showVendorID
              buttonCaption="Update Customer"
              existingVendors={existingVendors}
            />
          )}
        </Formik>
      </div>

      {/* Fuzzy match dialog - shown when similar customer names are found */}
      <DuplicateNameDialog
        open={dialogState.open}
        onClose={handleDialogClose}
        onProceed={handleDialogProceed}
        matchType={dialogState.matchType}
        entityType="vendor"
        proposedName={pendingSubmission?.values?.VENDOR_NAME || ""}
        matches={dialogState.matches}
      />
    </>
  );
};

export default UpdateVendor;
